// RefinanceStart.jsx — Mortgage Matrix (Refinance Flow)
// v14.0 — Adds occupation fallback (jobTitle → occupation)

import React, { useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import styles from "./RefinanceForm.module.css";

const TWILIO_API_URL =
  "https://fox00890i2.execute-api.ap-southeast-2.amazonaws.com/prod/twilio";

const LOOKUP_URL =
  "https://7r69rk0g8a.execute-api.ap-southeast-2.amazonaws.com/default/MortgageMatrixGetLeadByMobile";

const TEST_MODE = true;

function RefinanceStart({ formData, setFormData, nextStep }) {
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      applicant1: {
        ...(formData.applicant1 || {}),
        phone: e.target.value,
      },
    });
  };

  // -------------------------------------------------------------
  // Normalise AU mobile
  // -------------------------------------------------------------
  const normaliseAU = (n = "") => {
    let x = n.replace(/\D/g, "");
    if (x.startsWith("61")) x = "0" + x.slice(2);
    if (!x.startsWith("0")) x = "0" + x;
    return x.slice(0, 10);
  };

  // -------------------------------------------------------------
  // Prefill from Mercury
  // -------------------------------------------------------------
  const fetchExisting = async (mobile04) => {
    console.log("🔍 Checking Mercury for mobile:", mobile04);

    try {
      const resp = await fetch(LOOKUP_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mobile: mobile04 }),
      });

      const txt = await resp.text();
      let data = {};

      try {
        data = JSON.parse(txt);
      } catch {
        toast.error("Invalid Mercury response.");
        return;
      }

      if (!data.success) {
        toast.error("Mercury lookup error.");
        return;
      }

      if (!data.found) {
        toast.info("No existing record found in Mercury.");

        setFormData((prev) => ({
          ...prev,
          applicant1: {
            ...(prev.applicant1 || {}),
            phone: mobile04,
          },
        }));

        return;
      }

      toast.success("Prefill found in Mercury.");

      // -------------------------------------------------------------
      // Applicant 1 Income (data.employmentIncomes[])
      // -------------------------------------------------------------
      let applicant1Income = {};

      if (Array.isArray(data.employmentIncomes) && data.employmentIncomes.length > 0) {
        const inc = data.employmentIncomes[0];

        applicant1Income = {
          employmentType: inc.employmentType || "",
          employerName: inc.employmentType === "PAYG" ? inc.employerName : "",
          businessName: inc.employmentType === "Self Employed" ? inc.employerName : "",

          // occupation fallback here:
          description: inc.jobTitle || inc.occupation || "",

          startDate: inc.startDate || "",
          incomeAmount: inc.amount || "",
          otherIncomeType: "",
          otherIncomeAmount: "",
          otherIncomeFrequency: "",
        };
      }

      // Optional other income for applicant 1
      if (Array.isArray(data.otherIncomes) && data.otherIncomes.length) {
        const oi = data.otherIncomes[0];
        applicant1Income = {
          ...applicant1Income,
          otherIncomeType: oi.type || "",
          otherIncomeAmount: oi.amount || "",
          otherIncomeFrequency: oi.frequency || "",
        };
      }

      // -------------------------------------------------------------
      // Applicant 2 Income
      // ✔ From data.coApplicant.employmentIncomes[]
      // -------------------------------------------------------------
      let applicant2Income = {};

      if (
        data.coApplicant &&
        Array.isArray(data.coApplicant.employmentIncomes) &&
        data.coApplicant.employmentIncomes.length > 0
      ) {
        const inc = data.coApplicant.employmentIncomes[0];

        applicant2Income = {
          employmentType: inc.employmentType || "",
          employerName: inc.employmentType === "PAYG" ? inc.employerName : "",
          businessName: inc.employmentType === "Self Employed" ? inc.employerName : "",

          // same occupation fallback mapping
          description: inc.jobTitle || inc.occupation || "",

          startDate: inc.startDate || "",
          incomeAmount: inc.amount || "",
          otherIncomeType: "",
          otherIncomeAmount: "",
          otherIncomeFrequency: "",
        };
      }

      // -------------------------------------------------------------
      // Build Updated formData
      // -------------------------------------------------------------
      const updated = {
        ...formData,

        applicant1: {
          ...(formData.applicant1 || {}),
          firstName: data.firstName || "",
          lastName: data.lastName || "",
          email: data.email || "",
          phone: data.mobile || mobile04,
          maritalStatus: data.maritalStatus || "",
          dependants: data.dependants ?? "",
        },

        refinancingWithSomeone: !!data.coApplicant,

        applicant2: data.coApplicant
          ? {
              ...(formData.applicant2 || {}),
              firstName: data.coApplicant.firstName || "",
              lastName: data.coApplicant.lastName || "",
              email: data.coApplicant.email || "",
              phone: data.coApplicant.mobile || "",
            }
          : {},

        income: {
          applicant1: applicant1Income,
          ...(data.coApplicant && { applicant2: applicant2Income }),
        },

        // Refinance prefill
        mercuryContactId: data.contactId,
        mercuryOpportunityId: data.opportunityId,
        propertyAddress: data.propertyAddress || "",
        propertyValue: data.propertyValue || "",
        loanBalance: data.loanBalance || "",
        interestRate: data.interestRate || "",
        currentLender: data.currentLender || "",
        properties: data.properties || [],
        mortgageLoans: data.mortgageLoans || [],
        creditCards: data.creditCards || [],
        personalLoans: data.personalLoans || [],
        carLoans: data.carLoans || [],
        hecs: data.hecs || [],
      };

      setFormData(updated);
    } catch (err) {
      console.error("❌ Mercury Lookup Error:", err);
      toast.error("Lookup failed.");
    }
  };

  // -------------------------------------------------------------
  // Test Mode Next (no OTP)
  // -------------------------------------------------------------
  const handleTestNext = async () => {
    const raw = formData.applicant1?.phone || "";
    if (!raw) return toast.error("Enter a mobile number.");

    const mobile04 = normaliseAU(raw);

    setLoading(true);
    await fetchExisting(mobile04);
    setLoading(false);

    toast.success("Mobile verified (TEST MODE)");
    nextStep();
  };

  return (
    <div className={styles.container}>
      <div className={styles.formRow}>
        <input
          type="tel"
          placeholder="04xxxxxxxx"
          value={formData.applicant1?.phone || ""}
          onChange={handleChange}
          maxLength="10"
          className={styles.fullWidthInput}
        />

        {TEST_MODE && (
          <button
            className={`${styles.nextBtn} ${styles.fullWidthBtn}`}
            onClick={handleTestNext}
            disabled={loading}
          >
            {loading ? "Checking…" : "Next →"}
          </button>
        )}
      </div>

      {TEST_MODE && (
        <p style={{ textAlign: "center", color: "orange", marginTop: "10px" }}>
          ⚙️ Test Mode Active — OTP Skipped
        </p>
      )}

      <ToastContainer position="top-center" autoClose={3000} hideProgressBar />
    </div>
  );
}

export default RefinanceStart;